import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState, AppThunk } from "../../app/store";

import { Metadata, Content } from "../../util/types";
import { Contact } from "../contact/contactSlice";

/*
export interface FileMetadata {
    id: string;
    user_id: string;
    page_id: string;
    organization_id: string;
    created: string;
    modified: string;
    deleted: string | null;
	name: string;
	extension: string;
	has_thumbnail: boolean;
	content: string;
};
// */

export interface FileMetadata {
    metadata: Metadata;
    name: string;
    extension: string;
    star: boolean;
    image_profile: string;
    page_id: string;
    tag: string;
    content: string;
    labels: string[];
    contacts: string[];
}

export interface FileMetadataItem {
    metadata: Metadata;
    name: string;
    extension: string;
    star: boolean;
    image_profile: string;
    page_id: string;
    tag: string;
    content: string;
}

export interface FileMetadataEditRequest {
    metadata: Metadata;
    name: string;
    extension: string;
    image_profile: string;
    page_id: string;
    content: string;
    labels: Contact[];
    contacts: { [key: string]: Contact };
    tag: string;
    star: boolean;
}

export interface FileMetadataEditResponse {
    file: FileMetadataItem;
    labels: { [key: string]: string };
    contacts: { [key: string]: Contact };
}

export interface FileState {
    store: { [key: string]: FileMetadataItem };
    order: string[];
}

const initialState: FileState = {
    store: {},
    order: ["Images", "NonImages"],
};

export interface FileMetadataListResult {
    files: { [key: string]: FileMetadataItem };
}

export function toEditRequest(
    file: FileMetadataEditResponse,
): FileMetadataEditRequest {
    return {
        metadata: file.file.metadata,
        name: file.file.name,
        extension: file.file.extension,
        image_profile: file.file.image_profile,
        page_id: file.file.page_id,
        content: file.file.content,
        labels: [],
        contacts: file.contacts,
        tag: "",
        star: false,
    };
}
/*
 * use example: const [images, nonImages] = partitionObject(store, isImage)
 * */
function partitionObject<T>(
    obj: { [key: string]: T },
    predicate: (value: T, key: string) => boolean,
): [{ [key: string]: T }, { [key: string]: T }] {
    return Object.entries(obj).reduce<
        [{ [key: string]: T }, { [key: string]: T }]
    >(
        ([pass, fail], [key, value]) => {
            if (predicate(value, key)) {
                pass[key] = value;
            } else {
                fail[key] = value;
            }
            return [pass, fail];
        },
        [{}, {}],
    );
}

/*
function partitionFiles(
  obj: { [key: string]: FileMetadata },
  predicate: (value: FileMetadata, key: string) => boolean
): [{ [key: string]: FileMetadata }, { [key: string]: FileMetadata }] {
  return Object.entries(obj).reduce<
    [{ [key: string]: FileMetadata }, { [key: string]: FileMetadata }]
  >(
    ([pass, fail], [key, value]) => {
      if (predicate(value, key)) {
        pass[key] = value;
      } else {
        fail[key] = value;
      }
      return [pass, fail];
    },
    [{}, {}]
  );
}
// */

export function partitionByImages(
    obj: Record<string, FileMetadataItem>,
): [Record<string, FileMetadataItem>, Record<string, FileMetadataItem>] {
    const images: Record<string, FileMetadataItem> = {};
    const nonImages: Record<string, FileMetadataItem> = {};

    for (const [key, meta] of Object.entries(obj)) {
        const lower = meta.extension.toLowerCase();
        if (
            lower.endsWith("png") ||
            lower.endsWith("jpg") ||
            lower.endsWith("jpeg") ||
            lower.endsWith("webp") ||
            lower.endsWith("gif")
        ) {
            images[key] = meta;
        } else {
            nonImages[key] = meta;
        }
    }

    return [images, nonImages];
}

const isImage = (meta: FileMetadataItem) => {
    const lower = meta.extension.toLowerCase();
    return (
        lower.endsWith(".png") ||
        lower.endsWith(".jpg") ||
        lower.endsWith(".jpeg") ||
        lower.endsWith(".webp")
    );
};

/*
export function partitionByImages(
  obj: { [key: string]: FileMetadata },
): [{ [key: string]: FileMetadata }, { [key: string]: FileMetadata }] {
    return partitionFiles(obj, isImage);
}
// */

export const fileSlice = createSlice({
    name: "file",
    initialState,
    // The `reducers` field lets us define reducers and generate associated actions
    reducers: {
        upsertFile: (state, action: PayloadAction<FileMetadataItem>) => {
            const id = action.payload.metadata.id;
            if (id) {
                state.store[id] = action.payload;
                return state;
            } else {
                return state;
            }
        },
        upsertFiles: (
            state,
            action: PayloadAction<{ [key: string]: FileMetadataItem }>,
        ) => {
            state.store = { ...state.store, ...action.payload };
        },
    },
});

export const { upsertFile, upsertFiles } = fileSlice.actions;

// The function below is called a selector and allows us to select a value from
// the state. Selectors can also be defined inline where they're used instead of
// in the slice file. For example: `useSelector((state: RootState) => state.counter.value)`
export const selectCount = (state: RootState) => state.counter.value;

// We can also write thunks by hand, which may contain both sync and async logic.
// Here's an example of conditionally dispatching actions based on current state.
export default fileSlice.reducer;
